/**
 * Sport domain logic — pure functions, no React and no storage.
 *
 * A sport habit is a normal habit (same schedule / drag / delete rules) with
 * `kind: 'sport'` and an extra `sport` blueprint describing what to log.
 *
 * DATA MODEL
 * ----------
 * SportConfig (stations mode) {
 *   mode:     'stations'
 *   evaluate: boolean                   show the progress tab in the session
 *   stations: [{ id, name, sets: number, metrics: [{ id, name }] }]
 * }
 *
 * SportConfig (single mode — running, swimming, cycling) {
 *   mode:     'single'
 *   evaluate: boolean
 *   stations: [{ id: 'main', name, sets: 1, metrics: [{ id, name }] }]
 * }
 *
 * Reps are not part of the blueprint: if you want to record them, add "reps"
 * as one of the metrics like any other number.
 *
 * Single mode is stored as a one-station / one-set blueprint on purpose: every
 * renderer, key builder and completeness check then has exactly one shape to
 * deal with, and the UI is the only place that knows the difference.
 *
 * LOGS
 * ----
 * Recorded numbers live outside the habit, in a flat map so a value survives
 * dragging the occurrence to another day:
 *
 *   sportLogs['<habitId>|<baseDateKey>'] = { '<stationId>:<setIndex>:<metricId>': '72.5' }
 *
 * Values are kept as strings — exactly what the user typed — and only parsed
 * when something needs a number.
 */

export const SPORT_KIND = 'sport';
export const HABIT_KIND = 'habit';

export const MODE_STATIONS = 'stations';
export const MODE_SINGLE = 'single';

/** Station id used by single-step activities, which have only one. */
export const SINGLE_STATION_ID = 'main';

/** Guard rails so a typo in the modal cannot create a 10.000-row session. */
export const MAX_SETS = 20;
export const MAX_METRICS = 6;
export const MAX_STATIONS = 20;

/** Generate a reasonably unique id without pulling in a dependency. */
export function makeSportId(prefix = 's') {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Clamp a user-entered number into a sane integer range.
 * @param {unknown} value
 * @param {number} min
 * @param {number} max
 * @param {number} fallback used when the input is not a number at all
 * @returns {number}
 */
export function clampInt(value, min, max, fallback = min) {
  // Guard the blank string explicitly: Number('') is 0, which would silently
  // turn a cleared input into the minimum instead of leaving it alone.
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

/**
 * A blank metric (one numeric column to report), ready for the editor.
 * @param {string} [name]
 * @returns {{id: string, name: string}}
 */
export function createMetric(name = '') {
  return { id: makeSportId('m'), name };
}

/**
 * Copy a list of metrics for a new station: same names, fresh ids.
 *
 * Stations in one workout almost always report the same numbers (kg, reps…),
 * so the second station starts from the first one's columns instead of an
 * empty row. Unnamed drafts are skipped, and an empty result still yields one
 * blank metric so the editor never renders nothing.
 *
 * @param {{name?: string}[]} [metrics]
 * @returns {{id: string, name: string}[]}
 */
export function cloneMetrics(metrics = []) {
  const named = (Array.isArray(metrics) ? metrics : [])
    .filter((metric) => metric && String(metric.name || '').trim())
    .slice(0, MAX_METRICS)
    .map((metric) => createMetric(String(metric.name).trim()));
  return named.length > 0 ? named : [createMetric()];
}

/**
 * A blank station with one metric already in place.
 * @param {Partial<{name: string, sets: number, metrics: object[]}>} [input]
 *   `metrics` seeds the value columns — pass the previous station's to inherit
 *   them; leave it out for a single blank one.
 * @returns {object} station
 */
export function createStation({ name = '', sets = 3, metrics } = {}) {
  return {
    id: makeSportId('st'),
    name,
    sets: clampInt(sets, 1, MAX_SETS, 1),
    metrics: metrics ? cloneMetrics(metrics) : [createMetric()],
  };
}

/**
 * A blank blueprint for the chosen mode.
 * @param {'stations'|'single'} mode
 * @returns {object} sport config
 */
export function createSportConfig(mode = MODE_STATIONS) {
  if (mode === MODE_SINGLE) {
    return {
      mode: MODE_SINGLE,
      evaluate: true,
      stations: [{ id: SINGLE_STATION_ID, name: '', sets: 1, metrics: [createMetric()] }],
    };
  }
  return { mode: MODE_STATIONS, evaluate: true, stations: [createStation()] };
}

/**
 * Storage/edit-time cleanup: drop unnamed metrics, clamp counts, keep shape.
 * Returns null when the config cannot be salvaged into something loggable.
 * @param {unknown} raw
 * @returns {object|null}
 */
export function normalizeSportConfig(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const mode = raw.mode === MODE_SINGLE ? MODE_SINGLE : MODE_STATIONS;
  const single = mode === MODE_SINGLE;
  const rawStations = Array.isArray(raw.stations) ? raw.stations.slice(0, MAX_STATIONS) : [];

  const stations = rawStations
    .map((station, index) => {
      if (!station || typeof station !== 'object') return null;

      // Metrics are the point of the whole feature — a station without a named
      // one has nothing to record, so it is dropped.
      const metrics = (Array.isArray(station.metrics) ? station.metrics : [])
        .slice(0, MAX_METRICS)
        .filter((metric) => metric && typeof metric.name === 'string' && metric.name.trim())
        .map((metric) => ({
          id: typeof metric.id === 'string' ? metric.id : makeSportId('m'),
          name: metric.name.trim(),
        }));
      if (metrics.length === 0) return null;

      return {
        id: single ? SINGLE_STATION_ID : String(station.id || makeSportId('st')),
        name: typeof station.name === 'string' ? station.name.trim() : '',
        // Single-step activities are logged exactly once.
        sets: single ? 1 : clampInt(station.sets, 1, MAX_SETS, 1),
        metrics,
      };
    })
    .filter(Boolean)
    // Single mode can only ever have the one station.
    .slice(0, single ? 1 : MAX_STATIONS);

  if (stations.length === 0) return null;
  return { mode, evaluate: Boolean(raw.evaluate), stations };
}

/**
 * Key of one numeric input inside a session log.
 * @param {string} stationId
 * @param {number} setIndex zero-based
 * @param {string} metricId
 * @returns {string}
 */
export function sportFieldKey(stationId, setIndex, metricId) {
  return `${stationId}:${setIndex}:${metricId}`;
}

/**
 * Take a field key apart again.
 * Ids never contain a colon, so a plain split is safe.
 * @param {string} fieldKey
 * @returns {{stationId: string, setIndex: number, metricId: string}|null}
 */
export function parseSportFieldKey(fieldKey) {
  const parts = String(fieldKey || '').split(':');
  if (parts.length !== 3) return null;
  const setIndex = Number(parts[1]);
  if (!Number.isInteger(setIndex) || setIndex < 0) return null;
  return { stationId: parts[0], setIndex, metricId: parts[2] };
}

/**
 * Every field a fully filled-in session must contain, in display order.
 * @param {object} sport
 * @returns {string[]}
 */
export function sportFieldKeys(sport) {
  const keys = [];
  for (const station of sport?.stations || []) {
    for (let setIndex = 0; setIndex < station.sets; setIndex += 1) {
      for (const metric of station.metrics) {
        keys.push(sportFieldKey(station.id, setIndex, metric.id));
      }
    }
  }
  return keys;
}

/**
 * Is a single logged value a usable number?
 * Empty strings, whitespace and junk all count as "not filled in yet".
 * @param {unknown} value
 * @returns {boolean}
 */
export function isFilledValue(value) {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  if (!text) return false;
  return Number.isFinite(Number(text));
}

/**
 * How much of a session is done — drives both the progress hint and the tick.
 * @param {object} sport
 * @param {Record<string, string>} [values]
 * @returns {{filled: number, total: number}}
 */
export function countSportProgress(sport, values = {}) {
  const keys = sportFieldKeys(sport);
  return {
    filled: keys.filter((key) => isFilledValue(values[key])).length,
    total: keys.length,
  };
}

/**
 * A sport habit turns green only once every numeric field carries a number.
 * @param {object} sport
 * @param {Record<string, string>} [values]
 * @returns {boolean}
 */
export function isSportComplete(sport, values = {}) {
  const { filled, total } = countSportProgress(sport, values);
  return total > 0 && filled === total;
}

/**
 * Set one value, returning a new log object. Blank input clears the field so a
 * session can go back to "unfinished" after an accidental entry.
 * @param {Record<string, string>} values
 * @param {string} fieldKey
 * @param {string} value
 * @returns {Record<string, string>}
 */
export function setSportValue(values, fieldKey, value) {
  const next = { ...values };
  if (String(value).trim() === '') delete next[fieldKey];
  else next[fieldKey] = String(value);
  return next;
}

/**
 * Copy the first set's number down the remaining sets of the same station.
 *
 * Most people repeat a weight across all sets, so typing it once is enough.
 * Two rules keep it out of the way:
 *  - a set the user typed into themselves is never overwritten;
 *  - a set still showing the first set's *previous* value is treated as an
 *    untouched echo and follows along, so typing "7" then "2" ends up as 72
 *    everywhere rather than leaving 7 behind.
 *
 * Clearing the first set clears its echoes too, which puts the session back to
 * "unfinished" instead of stranding numbers nobody entered.
 *
 * @param {object} sport blueprint
 * @param {Record<string, string>} values log *after* the edit
 * @param {string} fieldKey field that was just edited
 * @param {string|undefined} previousSeed what that field held before the edit
 * @returns {Record<string, string>}
 */
export function autofillSets(sport, values, fieldKey, previousSeed) {
  const parsed = parseSportFieldKey(fieldKey);
  // Only the first set seeds the others — editing set 3 stays local.
  if (!parsed || parsed.setIndex !== 0) return values;

  const station = (sport?.stations || []).find((entry) => entry.id === parsed.stationId);
  if (!station || station.sets < 2) return values;

  const seed = values[fieldKey];
  const next = { ...values };

  for (let setIndex = 1; setIndex < station.sets; setIndex += 1) {
    const key = sportFieldKey(parsed.stationId, setIndex, parsed.metricId);
    const current = next[key];
    const echoed = previousSeed !== undefined && current === previousSeed;
    if (isFilledValue(current) && !echoed) continue;

    if (isFilledValue(seed)) next[key] = seed;
    else delete next[key];
  }
  return next;
}

/**
 * Record one value and spread it across the station's other sets.
 * The single entry point the UI uses, so both halves stay in step.
 * @param {object} sport
 * @param {Record<string, string>} values
 * @param {string} fieldKey
 * @param {string} value
 * @returns {Record<string, string>}
 */
export function applySportValue(sport, values, fieldKey, value) {
  const previousSeed = values?.[fieldKey];
  return autofillSets(sport, setSportValue(values, fieldKey, value), fieldKey, previousSeed);
}

/**
 * Strip values whose field no longer exists in the blueprint.
 * Cheap insurance against logs growing forever if a config ever changes.
 * @param {object} sport
 * @param {Record<string, string>} values
 * @returns {Record<string, string>}
 */
export function pruneSportValues(sport, values = {}) {
  const allowed = new Set(sportFieldKeys(sport));
  return Object.fromEntries(Object.entries(values).filter(([key]) => allowed.has(key)));
}

/** True for habits created through the sport modal. */
export function isSportHabit(habit) {
  return habit?.kind === SPORT_KIND && Boolean(habit.sport);
}

/**
 * Human-readable one-liner for a blueprint, shown under the card title.
 * @param {object} sport
 * @returns {string}
 */
export function describeSport(sport) {
  if (!sport) return '';
  if (sport.mode === MODE_SINGLE) {
    const count = sport.stations[0]?.metrics.length || 0;
    return `${count} value${count === 1 ? '' : 's'}`;
  }
  const stations = sport.stations.length;
  const sets = sport.stations.reduce((total, station) => total + station.sets, 0);
  return `${stations} station${stations === 1 ? '' : 's'} · ${sets} set${sets === 1 ? '' : 's'}`;
}
